import * as React from 'react';
import * as classNames from 'classnames';
import { graphql, ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import { get, times } from 'lodash';
import { parse, stringify } from 'qs';

import CollapsingFacet from '../../components/collapsing_facet';
import { CommonsCmsSubcategoryNav } from '../../rql';
import CoreLink from '../../components/core_link';
import { IDynamicComponentProps } from '../dynamic_component_props';

// this value must match the `nth-child` selector value in subcategory-nav.scss
const CMS_COLLAPSE_THRESHOLD = 11;

export interface IProps extends IDynamicComponentProps {
  categoryUUID: string;
  linkOverride?: string;
  jumplinkSlug?: string;
}

export class SubcategoryNav extends React.Component<ChildProps<IProps, CommonsCmsSubcategoryNav.Query>, null> {
  renderplaceholderNav() {
    return (
      <div className="facet">
        <nav>
          <ul className="cms-facet__options cms-facet__options--placeholder">
            {times(6, (idx) => {
              return (
                <li className="cms-facet__option" key={idx}>
                  <a href="#" className="cms-facet__link">
                    <div className="cms-facet__link__image__wrap">
                      <div className="cms-facet__link__image" />
                    </div>
                    <div className="cms-facet__link__text" />
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    );
  }

  buildLink(category: CommonsCmsSubcategoryNav.Categories): string {
    if (this.props.linkOverride) {
      const parts = this.props.linkOverride.split('?');
      const path = parts[0];
      let query = parse(parts[1]);

      if (!category.rootSlug) {
        query = stringify({ product_type: category.slug, ...query });
      } else {
        query = stringify({
          product_type: category.rootSlug,
          category: category.slug,
          ...query,
        });
      }

      return `${path}?${query}`;
    }

    return category.cmsLink.href;
  }

  getBackgroundImageStyle(cat) {
    const src = get(cat.images[0], 'source');
    if (!src) return null;

    return { backgroundImage: `url(${src})` };
  }

  renderParentCategories(parentCategories) {
    if (parentCategories.length === 0) return null;

    return (
      <div className="cms-facet__link__parents-wrapper">
        {parentCategories.map((parent, idx) => (
          <CoreLink
            to={this.buildLink(parent)}
            entityId={parent.id}
            className="cms-facet__link cms-facet__link--parent"
            key={idx}
            position={idx}
          >
            <span>{parent.name}</span>
          </CoreLink>
        ))}
      </div>
    );
  }

  categories() {
    return this.props.data.categories || [];
  }

  renderChildrenCategories(childCategories) {
    if (childCategories.length === 0) return null;

    return (
      <CollapsingFacet
        count={childCategories.length}
        countThreshold={CMS_COLLAPSE_THRESHOLD}
        classPrefix="cms-facet"
      >
        <ul className="cms-facet__options">
          {childCategories.map((cat, idx) => (
            <li
              key={idx}
              className="cms-facet__option"
            >
              <CoreLink
                to={this.buildLink(cat)}
                entityId={cat.id}
                className="cms-facet__link"
                key={idx}
                position={idx}
              >
                <div className="cms-facet__link__image__wrap">
                  <div
                    className="cms-facet__link__image"
                    style={this.getBackgroundImageStyle(cat)}
                  />
                </div>
                <div className="cms-facet__link__text">
                  {cat.name}
                </div>
              </CoreLink>
            </li>
          ))}
        </ul>
      </CollapsingFacet>
    );
  }

  render() {
    if (this.props.data.loading) return this.renderplaceholderNav();
    if (!this.props.data.loading && !this.categories().length) {
      return null;
    }

    const category = this.categories()[0];
    const classes = classNames(
      'cms-facets',
      { 'cms-facets--subcategory': !!category.parentCategories },
    );

    return (
      <div
        className={classes}
        id={this.props.jumplinkSlug || null}
      >
        <nav>
          {this.renderParentCategories(category.parentCategories)}
          <div className="cms-facets__wrapper">
            <div className="cms-facet__link cms-facet__link--current">
              {category.name}
            </div>
            {this.renderChildrenCategories(category.childCategories)}
          </div>
        </nav>
      </div>
    );
  }
}

const connect = graphql<IProps, CommonsCmsSubcategoryNav.Query, CommonsCmsSubcategoryNav.Variables>(
  gql`
  query Commons_Cms_SubcategoryNav(
    $ids: [String]
  )
  {
    categories(input:{
      ids: $ids,
      withChildren: true,
      withParent: true,
    }) {
      _id
      id
      name
      slug
      rootSlug
      cmsLink {
        href
      }
      images(input:{
        type:"Category",
        count:1,
        transform:"card_wide",
        scope:"full_bleed_brand"
      }) {
        source
      }
      childCategories(input: {
        withAggregations: CATEGORY_UUIDS
      }) {
        _id
        id
        name
        slug
        rootSlug
        cmsLink {
          href
        }
        images(input:{
          type:"Category",
          count:1,
          transform:"card_wide",
          scope:"full_bleed_brand"
        }) {
          source
        }
      }
      parentCategories {
        _id
        id
        name
        slug
        rootSlug
        cmsLink {
          href
        }
        images(input:{
          type:"Category",
          count:1,
          transform:"card_wide",
          scope:"full_bleed_brand"
        }) {
          source
        }
      }
    }
  }
  `,
  {
    options: ownProps => ({
      ssr: ownProps.ssr,
      variables: {
        ids: [ownProps.categoryUUID],
      },
    }),
  },
);

export default connect(SubcategoryNav);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/cms/components/subcategory_nav.tsx